<script lang="ts">
  import type { EnquiryData } from "./services/enquiryData";
  import { EnquiryConfig, type WidgetLayout } from "./services/config";
  import type { TravelDataFormValue, FilterFormValue, PersonalDataFormValue } from "./services/formModels";
  import Stepper, { StepperControl } from "../stepper/Stepper.svelte";
  import StepperLine from "../stepper/StepperLine.svelte";
  import StepperStep from "../stepper/StepperStep.svelte";
  import { FormControl } from "../forms/controls";
  import EnquiryTravelData from "./EnquiryTravelData.svelte";
  import EnquiryFilterForm from "./EnquiryFilterForm.svelte";
  import EnquiryAccosForm from "./EnquiryAccosForm.svelte";
  import EnquiryPersonalData from "./EnquiryPersonalData.svelte";
  import EnquiryThankyou from "./EnquiryThankyou.svelte";
  import ImageTravelData from "./images/ImageTravelData.svelte";
  import ImageFilter from "./images/ImageFilter.svelte";
  import ImageAccos from "./images/ImageAccos.svelte";
  import ImagePersonalData from "./images/ImagePersonalData.svelte";
  import ImageThankyou from "./images/ImageThankyou.svelte";
  import { onMount } from "svelte";
  import deepmerge from "deepmerge";
  import { EnquiryTranslations } from "./Enquiry.i18n";
  import { EnquiryService } from "./services/enquiryService";

  export let host: HTMLElement;
  export let config: string;
  export let layout: WidgetLayout;

  onMount(() => {
    /**
     * public api
     */
    host.dispatchEvent(new Event("load"));
  });

  config ||= "enquiryForm";
  if (!globalThis[config]) throw new Error(`No config object was set. Please set 'window.${config}' to a valid config object.`);
  let options = new EnquiryConfig(globalThis[config]);
  const enquiryService = new EnquiryService(options);
  const defaultValue = options.value;

  $: T = EnquiryTranslations(options.language);

  let stepper = new StepperControl();
  let travelDataForm = new FormControl<TravelDataFormValue>("travelData");
  let filterForm = new FormControl<FilterFormValue>("filter");
  let personalDataForm = new FormControl<PersonalDataFormValue>("personalData");
  let accosSubtitle: string;
  let successHtml: string;

  // init
  setValue(defaultValue);

  /**
   * public api
   */
  export function setValue(value?: Partial<EnquiryData>) {
    if (value) {
      const { travelData, filterData, personalData } = enquiryService.getFormValues(value);
      travelDataForm.value = travelData;
      filterForm.value = filterData;
      personalDataForm.value = personalData;
    }
  }

  /**
   * public api
   */
  export function getValue() {
    return enquiryService.getEnquiryData(travelDataForm.value, filterForm.value, personalDataForm.value);
  }

  /**
   * public api
   */
  export function reset() {
    setValue(defaultValue);
    stepper.step = 1;
    stepper.update();
  }
</script>

<div class="enquiry layout-{layout}">
  <Stepper bind:stepper>
    <StepperStep name={T.get("travelData.title")}>
      <div class="step-layout">
        <div class="step-image">
          <ImageTravelData />
        </div>
        <div class="step-title">
          {T.get("travelData.title")}
        </div>
        <div class="step-line">
          <StepperLine />
        </div>
        <div class="step-form">
          <EnquiryTravelData
            {enquiryService}
            {layout}
            {T}
            bind:form={travelDataForm}
            on:submit={(_) => {
              const { hasPets, boardingIds } = travelDataForm.value;

              filterForm.value = deepmerge(filterForm.value, {
                themesAmenities: {
                  amenetyIds: hasPets ? [174] : [],
                },
                accotypes: {
                  boardingIds: boardingIds,
                },
              });

              const distinct = (array) => {
                return array.filter((x, i) => array.indexOf(x) == i);
              };

              filterForm.value.themesAmenities.amenetyIds = distinct(filterForm.value.themesAmenities.amenetyIds || []);
              filterForm.value.accotypes.boardingIds = distinct(filterForm.value.accotypes.boardingIds || []);

              stepper.next();
            }}
          />
        </div>
      </div>
    </StepperStep>

    {#if enquiryService.options.enquiryType == 1}
      <StepperStep name={T.get("filter.title")}>
        <div class="step-layout">
          <div class="step-image">
            <ImageFilter />
          </div>
          <div class="step-title">
            <span>{T.get("filter.title")}</span>
          </div>
          <div class="step-line">
            <StepperLine />
          </div>
          <div class="step-form">
            <EnquiryFilterForm {enquiryService} {layout} {T} range={travelDataForm.value.range} bind:form={filterForm} on:submit={(_) => stepper.next()} on:back={(_) => stepper.prev()} />
          </div>
        </div>
      </StepperStep>

      <!-- <StepperStep name={T.get("accos.title")} hidden>
        <div class="step-layout">
          <div class="step-image">
            <ImageAccos />
          </div>
          <div class="step-title">
            <div>
              <div>{T.get("accos.title")}</div>
              <div style="font-size: 18px; font-weight: normal;">{accosSubtitle || ""}</div>
            </div>
          </div>
          <div class="step-line">
            <StepperLine />
          </div>
          <div class="step-form">
            <EnquiryAccosForm {enquiryService} {layout} {T} bind:subtitle={accosSubtitle} bind:form={filterForm} on:submit={(_) => stepper.next()} on:back={(_) => stepper.prev()} />
          </div>
        </div>
      </StepperStep> -->
    {/if}

    <StepperStep name={T.get("personalData.title")}>
      <div class="step-layout">
        <div class="step-image">
          <ImagePersonalData />
        </div>
        <div class="step-title">
          <span>{T.get("personalData.title")}</span>
        </div>
        <div class="step-line">
          <StepperLine />
        </div>
        <div class="step-form">
          <EnquiryPersonalData
            {enquiryService}
            {layout}
            {T}
            travelData={travelDataForm.value}
            filter={filterForm.value}
            bind:form={personalDataForm}
            on:submit={(_) => {
              successHtml = _.detail;
              stepper.next();
            }}
            on:back={(_) => stepper.prev()}
          />
        </div>
      </div>
    </StepperStep>

    <StepperStep name={T.get("thankyou.title")}>
      <div class="step-layout">
        <div class="step-image">
          <ImageThankyou />
        </div>
        <div class="step-title">
          <span>{T.get("thankyou.title")}</span>
        </div>
        <div class="step-line">
          <StepperLine />
        </div>
        <div class="step-form">
          <EnquiryThankyou {T} {successHtml} />
        </div>
      </div>
    </StepperStep>
  </Stepper>
</div>

<style lang="scss">
  @import "./_mixins.scss";

  .step-image {
    grid-area: image;
    padding-right: 5em;
    max-height: 500px;

    :global(svg) {
      width: 100%;
      height: 100%;
    }
  }

  .step-line {
    grid-area: line;
    display: flex;
    align-items: flex-end;
  }

  .step-title {
    grid-area: title;
    font-size: 1.3125rem;
    font-size: var(--font-size-h4);
    font-weight: 600;
    margin-bottom: .2em;
  }

  .step-form {
    grid-area: form;
  }

  // layouts:
  .layout-default {
    .step-layout {
      display: grid;
      grid-template:
        "image title" max-content
        "image line" auto
        "image form" 1fr/1fr minmax(620px, 50%);
      gap: 20px;

      @include media-breakpoint-mobile {
        grid-template:
          "title" auto
          "line" auto
          "form" auto / 100%;
        gap: 20px;

        .step-image {
          display: none;
        }
      }
    }
  }

  .layout-simple {
    .step-layout {
      background-color: #f7f7f7;
      border: solid 2px #d9d9d9;
      border-radius: 28px;
      padding: 1.2em;
      padding-bottom: 0;

      display: grid;
      grid-template:
        "title" auto
        "line" auto
        "form" auto / 100%;
      gap: 20px;

      .step-image {
        display: none;
      }
    }
  }
</style>
